import {
  Badge,
  Checkbox,
  Flex,
  Icon,
  IconButton,
  Image,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { FC } from 'react';
import { FaExternalLinkAlt, FaRegImage, FaTrash } from 'react-icons/fa';
import { ArquivoDeMidia } from '../models';
import {
  acimaDoLimite,
  descreverUso,
  formatarData,
  formatarPeso,
  tipoDoArquivo,
} from '../utils';

interface Props {
  arquivos: ArquivoDeMidia[];
  permiteSelecao: boolean;
  selecionados: Set<string>;
  onSelecionar: (id: string, selecionado: boolean) => void;
  onSelecionarTodos: (selecionado: boolean) => void;
  onApagar: (arquivo: ArquivoDeMidia) => void;
}

const Cabecalho: FC = ({ children }) => (
  <Th textColor="#469BFF" fontFamily="Poppins">
    {children}
  </Th>
);

const TabelaArquivos: FC<Props> = ({
  arquivos,
  permiteSelecao,
  selecionados,
  onSelecionar,
  onSelecionarTodos,
  onApagar,
}) => {
  const todosSelecionados =
    arquivos.length > 0 && arquivos.every(({ id }) => selecionados.has(id));

  return (
    <TableContainer
      bg="white"
      border="1px solid #D6E8FF"
      borderRadius="12px"
      flexShrink={0}
    >
      <Table variant="simple" size="sm">
        <Thead bg="#F9FDFF">
          <Tr>
            {permiteSelecao && (
              <Th>
                <Checkbox
                  isChecked={todosSelecionados}
                  onChange={evento => onSelecionarTodos(evento.target.checked)}
                  aria-label="Selecionar todas desta página"
                />
              </Th>
            )}
            <Cabecalho>Prévia</Cabecalho>
            <Cabecalho>Nome</Cabecalho>
            <Cabecalho>Tipo</Cabecalho>
            <Cabecalho>Peso</Cabecalho>
            <Cabecalho>Enviada em</Cabecalho>
            <Cabecalho>Usada em</Cabecalho>
            <Cabecalho>Ações</Cabecalho>
          </Tr>
        </Thead>
        <Tbody>
          {arquivos.map(arquivo => {
            const usos = descreverUso(arquivo.usadoEm);
            return (
              <Tr key={arquivo.id}>
                {permiteSelecao && (
                  <Td>
                    <Checkbox
                      isChecked={selecionados.has(arquivo.id)}
                      onChange={evento =>
                        onSelecionar(arquivo.id, evento.target.checked)
                      }
                      aria-label={`Selecionar ${arquivo.nomeOriginal}`}
                    />
                  </Td>
                )}
                <Td>
                  <Image
                    src={arquivo.url}
                    alt={arquivo.nomeOriginal ?? ''}
                    boxSize="56px"
                    objectFit="cover"
                    borderRadius="6px"
                    fallback={
                      <Flex
                        boxSize="56px"
                        align="center"
                        justify="center"
                        borderRadius="6px"
                        bg="gray.100"
                      >
                        <Icon as={FaRegImage} color="gray.400" />
                      </Flex>
                    }
                  />
                </Td>
                <Td maxW="280px" whiteSpace="normal" wordBreak="break-all">
                  {arquivo.nomeOriginal}
                </Td>
                <Td>{tipoDoArquivo(arquivo.mimetype)}</Td>
                <Td>
                  <Badge
                    colorScheme={acimaDoLimite(arquivo.kb) ? 'red' : 'green'}
                  >
                    {formatarPeso(arquivo.kb)}
                  </Badge>
                </Td>
                <Td>{formatarData(arquivo.enviadoEm)}</Td>
                <Td whiteSpace="normal">
                  {usos.length ? (
                    usos.map(uso => <Text key={uso}>{uso}</Text>)
                  ) : (
                    <Text color="gray.400">—</Text>
                  )}
                </Td>
                <Td>
                  <IconButton
                    as={Link}
                    href={arquivo.url}
                    isExternal
                    aria-label="Abrir imagem"
                    icon={<FaExternalLinkAlt />}
                    size="xs"
                    variant="ghost"
                    color="#469BFF"
                  />
                  <IconButton
                    aria-label="Apagar imagem"
                    icon={<FaTrash />}
                    size="xs"
                    variant="ghost"
                    color="#FF6565"
                    ml={1}
                    onClick={() => onApagar(arquivo)}
                  />
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default TabelaArquivos;
